<template>
  <div class="tw-p-4">
    <h3 class="tw-text-center">{{ $t('course.all_remaining_courses') }}</h3>
    <div v-if="isLoading" class="tw-text-center tw-mt-6">
      <van-loading size="24px" />
    </div>
    <div v-if="course.length > 0 || estimate_courses.length > 0">
      <div v-for="courseItem in course" :key="courseItem.id" class="tw-mt-6">
        <PatientRemainingCourseCard
          :course="courseItem"
          @click="handleClick(courseItem)"
        />
      </div>
      <div
        v-for="courseItem in estimate_courses"
        :key="courseItem.id"
        class="tw-mt-6"
      >
        <PatientRemainingEstimateCourseCard
          :course="courseItem"
          @click="handleClick(courseItem)"
        />
      </div>
    </div>
    <div v-else-if="!isLoading" class="tw-text-center tw-text-gray-500 tw-mt-6">
      <van-cell center :title="$t('course.no_remaining_courses')"> </van-cell>
    </div>
  </div>
</template>

<script setup lang="ts">
import PatientRemainingCourseCard from '@/components/CourseDetail/PatientRemainingCourseCard.vue'
import PatientRemainingEstimateCourseCard from '@/components/CourseDetail/PatientRemainingEstimateCourseCard.vue'
import { useRouter } from 'vue-router'
import { onMounted, ref } from 'vue'
import { getParamsFromLiffUrl } from '@/utils'
import { RemainingCourse, RemainingEstimateCourse } from '@/type/Course'
import { showNotify } from 'vant'
import { useCourseRepository } from '@/repositories/courseRepo'
import * as Sentry from '@sentry/vue'
import { useTranslation } from 'i18next-vue'

const { t } = useTranslation()
const router = useRouter()

const isLoading = ref(true)
const course = ref<RemainingCourse[]>([])
const estimate_courses = ref<RemainingEstimateCourse[]>([])

const liffId = ref('')
const clinicType = ref('')
const clinicUuid = ref('')
const lineUserId = ref('')

const handleClick = (courseItem: RemainingCourse | RemainingEstimateCourse) => {
  // Navigate พร้อม query parameters
  router.push({
    path: '/course_detail',
    query: {
      liffId: liffId.value,
      clinicType: clinicType.value,
      c: clinicUuid.value,
      u: courseItem.uuid,
      l: lineUserId.value, // line user id (ถ้ามี)
      cache: new Date().getTime(), // เพื่อป้องกัน cache
      from: 'list', // เพื่อบอกว่ามาจากหน้า list นะ เวลาเขารีเฟรชหน้า course detail จะได้ยังรู้ว่ามาจากหน้า list อยู่
    },
  })
}

onMounted(async () => {
  const params = getParamsFromLiffUrl(window.location.href)
  liffId.value = params.get('liffId') || ''
  clinicType.value = params.get('clinicType') || ''
  clinicUuid.value = params.get('c') || ''
  try {
    isLoading.value = true

    const courseRepository = useCourseRepository(clinicUuid.value)
    const response = await courseRepository.fetchPatientRemainingCourses()

    course.value = response.courses
    estimate_courses.value = response.estimate_courses
  } catch (error) {
    console.error('Error fetching remaining courses', error)
    Sentry.captureException(error, {
      tags: {
        feature: 'remaining-courses',
      },
      extra: {
        clinic_uuid: params.get('c'),
        line_user_id: params.get('l'),
      },
    })
    course.value = []
    estimate_courses.value = []
    showNotify({
      type: 'danger',
      message: t('course.fetch_error'),
    })
  } finally {
    isLoading.value = false
  }
})
</script>
